/**
 * Contracts.tsx — lista umów tenanta (klasa dokumentów 'contract'); kolumny DATA-DRIVEN z
 * katalogu pól modułu `contracts` (`GET /system-modules/contracts/fields`), mirror
 * `PurchaseInvoices.tsx` (strona wzorcowa silnika list).
 *
 * Moduł: crm_core (dokumenty w pakiecie bazowym, ADR-0083). Backend: GET /api/contracts
 * (Model B — widoczność per-firma).
 *
 * Konfiguracja kolumn umów (nadpisania, wykluczenia, remap sortu, kolumny satelity, slugi
 * `?columns=`, mapa id→slug, typ wiersza) żyje w `lib/documents/contract-columns` — wspólna ze
 * stroną i z sekcją kokpitu decyzji; kolumny procesu buduje wspólne `lib/documents/process-columns`.
 *
 * Katalog core `contracts` to WYŁĄCZNIE 14 pól WSPÓLNYCH 7 klas dokumentów
 * (`DOCUMENT_COMMON_CORE_FIELDS`, `seed-system-modules.ts`) — numer systemowy/tytuł/status/
 * pilność/data dokumentu + opiekun/firma/kategoria/źródło + pola bez `showInList`. Pola
 * SPECYFICZNE umowy (numer u klienta, status umowy, okres, wartość, strona główna) żyją w
 * satelicie `document_contract_details` i NIE mają odpowiednika w katalogu (`system_module_fields`)
 * — zostają statycznymi kolumnami poza `buildCoreColumns`/rejestrem nadpisań (nie mają `field`
 * do dopasowania po slugu; `CoreColumnOverride` jest wołany tylko dla pól z katalogu).
 *
 * `buildCoreColumns` generuje: numer systemowy (mono, override), tytuł (pogrubiony,
 * `hideable:false`, override), status DOKUMENTU (skryty domyślnie jak przed migracją, override —
 * odróżnić od „Status umowy" niżej, satelita), pilność + data dokumentu (generyk — NOWE kolumny,
 * katalog je oznacza `showInList=true`, strona ich dotąd nie renderowała, dryf domknięty przez
 * migrację). Firma/opiekun/źródło WYKLUCZONE z `buildCoreColumns` (`excludeSlugs`) — firma
 * przez `buildCompanyColumn`, opiekun/źródło przez wspólny
 * `buildDocumentCoreRefColumns({ withSource: true })` (5 klas STANDARD dzieli ten blok — umowy
 * MAJĄ kolumnę źródła na liście, w przeciwieństwie do faktur, `SourceBadge` osobno); kategoria
 * to kolumna ręczna `buildCategoryColumn` (w katalogu `category_id` ma `showInList: false`). Kolumny
 * procesu (aktywny proces / bieżący etap) — pola WIRTUALNE silnika (poza katalogiem), domyślnie
 * widoczne gdy moduł procesu aktywny (mirror `PurchaseInvoices.tsx`).
 *
 * Widoczność: brak układu → wszystkie dostępne widoczne; układ jest DOSŁOWNY (kolumna spoza
 * układu ukryta, nie wpychamy nowych „na chama"). `?columns=` (projekcja odpowiedzi) budowany z
 * aktualnie widocznych kolumn (`computeVisibleColumnParams`); SQL SELECT rdzenia po stronie
 * backendu zostaje pełny.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListViewModeToggle,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { listLoadRetry } from '@/components/configurable-table/load-error';
import {
  type ListResponseMeta,
  useServerListControls,
} from '@/components/configurable-table/useServerListControls';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DocumentTypePickerModal } from '@/components/documents/DocumentTypePickerModal';
import { IconButton } from '@/components/IconButton';
import { ModuleGuard } from '@/components/ModuleGuard';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import {
  buildContractDetailColumns,
  CONTRACT_ALWAYS_SLUGS,
  CONTRACT_COLUMN_COMPANIONS,
  CONTRACT_COLUMN_ID_TO_SLUG,
  CONTRACT_OVERRIDES,
  CONTRACT_PROCESS_LABEL_PREFIX,
  type ContractRow,
  CONTRACTS_EXCLUDE_SLUGS,
  CONTRACTS_SORT_SLUG_OVERRIDES,
} from '@/lib/documents/contract-columns';
import { buildDocumentCoreRefColumns } from '@/lib/documents/document-core-ref-columns';
import {
  buildDocumentListFilterParams,
  counterpartyText,
  useDocumentListExport,
} from '@/lib/documents/document-list-export';
import { buildProcessColumns } from '@/lib/documents/process-columns';
import {
  buildCategoryColumn,
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { listFilterSkipSlugs, useListFilterOptions } from '@/lib/list-filter-options';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';

const CONTRACTS_KEY = 'contracts';

// Pola rysowane nadpisaniem kolumny albo osobnym builderem nie pobierają słownika opcji filtra.
const FILTER_OPTIONS_SKIP_SLUGS = listFilterSkipSlugs(CONTRACT_OVERRIDES, CONTRACTS_EXCLUDE_SLUGS);

interface ListResponse {
  contracts: ContractRow[];
  limit: number;
  offset: number;
  total: number;
  /** Slugi kolumn CORE sortowalnych serwerowo (silnik list) — patrz `useServerListControls`. */
  meta?: ListResponseMeta;
  /** Etykiety pól `reference` (sibling do wierszy) — patrz `ExtRefLabels`. */
  extRefLabels?: ExtRefLabels;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function Contracts() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showTypePicker, setShowTypePicker] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [showDeleted, setShowDeleted] = useState(false);

  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  // Tryb widoku + tryby dostępne w bieżącym layoucie (raport tabeli) — wspólny hook.
  const viewModes = useListViewMode();
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  // Sort serwerowy + paginacja (silnik list) — wspólny hook, patrz useServerListControls.ts.
  // `sortableSlugs` — `meta.sortable` z ostatniej odpowiedzi listy (`listQ` niżej), zasilane
  // PO zapytaniu jako REALNY stan (nie ref, jak `getRefLabels`) — kolumny core muszą się
  // przeliczyć, gdy metadane dotrą (patrz `columns` niżej).
  const controls = useServerListControls({
    columnIdToSortSlug: CONTRACTS_SORT_SLUG_OVERRIDES,
  });
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Zmiana filtrów w nagłówku wraca na pierwszą stronę — DWA callsite'y (ListFilterBar +
  // ConfigurableTable), więc zawinięte w jeden stabilny handler.
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  const [confirmDelete, setConfirmDelete] = useState<ContractRow | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<ContractRow | null>(null);

  // Zaznaczenie wierszy (checkbox po lewej) → akcje na zaznaczonych z menu „Akcje" (PHASE 25).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Zbiór kolumn FAKTYCZNIE widocznych w tabeli teraz — zasilany przez
  // `onVisibleColumnsChange` (ConfigurableTable), odpala się przy pierwszym rozwiązaniu
  // widoczności i przy każdej zmianie (toggle w gear, zapis Kreatora Listy). `null` przed
  // pierwszym wywołaniem -> `visibleColumnParams` niżej spada na `listLayout`/`defaultVisible`.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);

  // Model B (widocznosc per-firma) — tryb "Wszystkie firmy" omija ?companyId=,
  // tryb konkretnej firmy dokleja go do fetchu i klucza zapytania (§4.8 CLAUDE.md).
  const scope = useActiveCompanyScope();
  const companyId = companyIdParam(scope);
  const { me } = useTenantAuth();
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);
  // Moduł procesu aktywny → kolumny przekrojowe (aktywny proces / bieżący etap) domyślnie
  // widoczne (gdy brak układu). Nieaktywny → domyślnie ukryte (backend i tak nie dokłada dla
  // nich danych) — mirror `PurchaseInvoices.tsx`.
  const isProcessModuleActive = useMemo(
    () => me?.modules?.some((m) => m.code === 'processes') ?? false,
    [me?.modules],
  );

  // Zmiana zakresu firmy zawęża wynik — reset strony jak przy filtrach/sorcie (inaczej user
  // może utknąć na pustej stronie N). Zaznaczenie czyścimy, bo zawężony wynik może już nie
  // zawierać zaznaczonych wierszy.
  useEffect(() => {
    controls.resetPage();
    setSelectedIds([]);
  }, [companyId, controls.resetPage]);

  // Katalog pól modułu `contracts` (core + ext) — źródło WSZYSTKICH kolumn (rdzennych i
  // dodatkowych) generowanych dynamicznie poniżej.
  const fieldsQ = useSystemModuleFields('contracts');
  // Listy wyboru w wierszu filtrów, które trzeba wczytać: pozycje słowników pól `select` (umowy
  // dziś nie mają słownikowych — status/pilność to enumy z kodu) i użytkownicy dla „Opiekuna".
  const filterOptions = useListFilterOptions(fieldsQ.data, FILTER_OPTIONS_SKIP_SLUGS);
  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany
  // niżej PO `listQ`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();
  // Buildery kolumn biorą getter etykiet odwołań jako parametr — tabela dostaje getter z odpowiedzi
  // listy, eksport CSV (E16) getter z odpowiedzi eksportu (etykiety WSZYSTKICH wierszy pliku).
  const extColumnsFor = useCallback(
    (getLabels: () => ExtRefLabels | undefined) =>
      buildExtColumns<ContractRow>(fieldsQ.data ?? [], t, getLabels),
    [fieldsQ.data, t],
  );
  const extColumns = useMemo(() => extColumnsFor(getRefLabels), [extColumnsFor, getRefLabels]);

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; garstka przez rejestr nadpisań).
  // Firma/opiekun/kategoria/źródło wykluczone (dedykowane buildery niżej).
  const coreColumnsFor = useCallback(
    (getLabels: () => ExtRefLabels | undefined) =>
      buildCoreColumns<ContractRow>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels: getLabels,
        overrides: CONTRACT_OVERRIDES,
        excludeSlugs: CONTRACTS_EXCLUDE_SLUGS,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
        selectOptions: filterOptions.selectOptions,
      }),
    [
      fieldsQ.data,
      t,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      filterOptions.selectOptions,
    ],
  );
  const coreColumns = useMemo(() => coreColumnsFor(getRefLabels), [coreColumnsFor, getRefLabels]);

  // Wymuszony layout listy z pozycji Kreatora menu, z której user przyszedł
  // (kartoteka-layout-per-menu) — ta sama zmienna `tableId` musi iść do WSZYSTKICH
  // konsumentów niżej (ListFilterBar, ConfigurableTable), inaczej widżety i tabela
  // renderują dwa różne layouty.
  const forcedListLayoutId = useForcedListLayoutId(CONTRACTS_KEY);
  const tableId = forcedListLayoutId
    ? `${CONTRACTS_KEY}::layout-${forcedListLayoutId}`
    : CONTRACTS_KEY;

  // Dokleja `?mi=<id>` do nawigacji w głąb (nowy/detal), żeby formularz umowy
  // znał kontekst pozycji menu (viewRef) — URL detalu jest osobną nawigacją, nie
  // dziedziczy query stringa listy.
  const withMenuItem = useWithMenuItem();

  // Layout zapisany przez usera (Kreator Listy) — źródło aktualnej widoczności kolumn dla
  // `visibleColumnParams` niżej (ConfigurableTable czyta ten sam cache po `tableId`).
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);

  // Kolumny przekrojowe silnika (poza katalogiem) — aktywny proces / bieżący etap. Domyślnie
  // widoczne gdy moduł procesu aktywny (mirror `PurchaseInvoices.tsx`) — inaczej byłyby
  // kolumnami samych myślników.
  const processColumns = useMemo<ColumnDefinition<ContractRow>[]>(
    () =>
      buildProcessColumns<ContractRow>({
        t,
        labelKeyPrefix: CONTRACT_PROCESS_LABEL_PREFIX,
        isSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        isProcessModuleActive,
      }),
    [t, controls.isCoreColumnSortable, sortableSlugs, isProcessModuleActive],
  );

  // Skład kolumn listy — JEDNO miejsce dla tabeli i pliku CSV (eksport podaje własny getter etykiet
  // i kolumny katalogu/dodatkowe zbudowane z tym getterem).
  const columnsFor = useCallback(
    (
      getLabels: () => ExtRefLabels | undefined,
      core: ColumnDefinition<ContractRow>[],
      ext: ColumnDefinition<ContractRow>[],
    ): ColumnDefinition<ContractRow>[] => [
      ...core,
      buildCompanyColumn<ContractRow>({ companies, getRefLabels: getLabels, t }),
      // Kategoria — wspólny builder (`@/lib/ext-list-columns`), JEDNO źródło prawdy dla
      // Kontrahentów (pilot) + 7 klas dokumentów.
      buildCategoryColumn<ContractRow>({
        t,
        resolveSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
      }),
      // Opiekun / źródło — wyekstrahowane do wspólnego buildera (6 list dokumentów kopiowały
      // ten sam blok 1:1) — `@/lib/documents/document-core-ref-columns`. `withSource: true` —
      // umowy/pisma/handlowe/wewnętrzne (STANDARD) MAJĄ kolumnę źródła na liście (faktury
      // renderują ją osobno przez `SourceBadge`, inny słownik).
      ...buildDocumentCoreRefColumns<ContractRow>({
        t,
        getRefLabels: getLabels,
        withSource: true,
        resolveSourceSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        resolveSourceFilterable: (slug) => controls.isCoreColumnFilterable(slug, filterableSlugs),
        // Filtr opiekuna: lista użytkowników (`record-select`), gdy serwer go serwuje i lista się wczytała.
        resolveOwnerFilterable: (slug) => controls.isCoreColumnFilterable(slug, filterableSlugs),
        ownerFilterOptions: filterOptions.ownerOptions,
      }),
      // Pola specyficzne umowy (satelita `document_contract_details`) — BEZ odpowiednika w
      // katalogu (`system_module_fields`), więc statyczne kolumny poza `buildCoreColumns`/
      // rejestrem nadpisań (nie mają `field` do dopasowania po slugu); patrz
      // `buildContractDetailColumns` w `lib/documents/contract-columns`. Filtr w wierszu filtrów
      // tych kolumn zależy od `meta.filterable` (po slugu backendu, bez remapu sortu).
      ...buildContractDetailColumns({
        t,
        isSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        isFilterable: (slug) => controls.isCoreColumnFilterable(slug, filterableSlugs),
      }),
      ...processColumns,
      ...ext,
    ],
    [
      companies,
      t,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      filterOptions.ownerOptions,
      processColumns,
    ],
  );
  const columns = useMemo(
    () => columnsFor(getRefLabels, coreColumns, extColumns),
    [columnsFor, getRefLabels, coreColumns, extColumns],
  );

  // Filtry do backendu jako pojedyncza tablica `headerFilters`:
  // (1) popup w nagłówku (mode=values/condition/range) +
  // (2) input „Szukaj…" pod headerem (konwersja na mode=condition contains).
  const viewHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];

    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }

    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }

    return merged;
  }, [headerFilters, debouncedColumnFilters, columns]);
  // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika) —
  // tylko zapytanie listy. Eksport CSV (E16) bierze same filtry widoku: miesiąc to nawigacja.
  const exportHeaderFilters =
    viewHeaderFilters.length > 0 ? JSON.stringify(viewHeaderFilters) : undefined;
  const serializedHeaderFilters = controls.calendarHeaderFilter
    ? JSON.stringify([...viewHeaderFilters, controls.calendarHeaderFilter])
    : exportHeaderFilters;

  // Kolumny formatowania warunkowego (Kreator Listy) mogą celować w kolumnę aktualnie
  // NIEwidoczną (schowaną, ale z aktywną regułą) — `extraColumnIds` dopisuje jej slug do
  // `?columns=`, żeby reguła miała dane do ewaluacji nawet gdy kolumna sama nie jest wyświetlana.
  const formattingRuleColumnIds = useMemo(
    () => listLayout?.layoutConfig?.formattingRules?.map((r) => r.columnId) ?? [],
    [listLayout?.layoutConfig?.formattingRules],
  );

  // Mapa id kolumny → slug backendu dla `?columns=`. id = slug dla kolumn katalogowych
  // (`buildCoreColumns`, przez pętlę niżej); dedykowane buildery i kolumny statyczne (satelita
  // umowy, bez odpowiednika w katalogu) mapowane jawnie.
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = { ...CONTRACT_COLUMN_ID_TO_SLUG };
    for (const c of coreColumns) map[c.id] = c.id; // id = slug (buildCoreColumns)
    return map;
  }, [coreColumns]);
  const extSlugSet = useMemo(() => new Set(extColumns.map((c) => c.id)), [extColumns]);

  // Kolumny aktualnie widoczne, jako slugi backendu (wspólny helper) — do zbudowania
  // `?columns=`, żeby ODPOWIEDŹ (JSON) i odczyt pól dodatkowych (ext) ograniczały się do
  // faktycznie renderowanych kolumn — SQL SELECT rdzenia po stronie backendu zostaje pełny
  // niezależnie od tego (patrz komentarz w `apps/api/lib/documents/contract-list-descriptor.ts`).
  // `gcrecord` w `alwaysSlugs` — czyta go akcja wiersza (restore/delete), nie jest kolumną.
  // Źródło widoczności: `visibleColumnIds` (efektywna widoczność z ConfigurableTable, patrz
  // `onVisibleColumnsChange` niżej) gdy już rozwiązana; przed pierwszym wywołaniem — fallback
  // na zapisany layout (`listLayout`) / `defaultVisible` (jak dotychczas).
  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns:
          visibleColumnIds !== null
            ? columns.map((c) => ({ id: c.id, visible: visibleColumnIds.includes(c.id) }))
            : listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet,
        alwaysSlugs: CONTRACT_ALWAYS_SLUGS,
        companions: CONTRACT_COLUMN_COMPANIONS,
        extraColumnIds: formattingRuleColumnIds,
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      visibleColumnIds,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extSlugSet,
      formattingRuleColumnIds,
    ],
  );

  // Parametry filtrów listy BEZ stronicowania i projekcji kolumn — wspólne dla zapytania listy
  // i eksportu CSV (E16: plik zawiera dokładnie to, co lista).
  function buildListFilterParams({
    includeCalendar,
  }: {
    includeCalendar: boolean;
  }): URLSearchParams {
    const listHeaderFilters = includeCalendar ? serializedHeaderFilters : exportHeaderFilters;
    return buildDocumentListFilterParams({
      search: debouncedSearch,
      showArchived: showDeleted,
      companyId,
      headerFilters: listHeaderFilters,
      sort: controls.queryParams.sort,
      order: controls.queryParams.order,
    });
  }

  const queryKey = [
    CONTRACTS_KEY,
    {
      q: debouncedSearch,
      include_deleted: showDeleted,
      companyId: companyId ?? 'all',
      headerFilters: serializedHeaderFilters,
      columns: visibleColumnParams,
      ...controls.queryParams,
    },
  ];

  const listQ = useQuery({
    queryKey,
    // Trzyma poprzednie dane na ekranie podczas refetchu wywołanego zmianą klucza (kolumny
    // widoczne się jeszcze „ustalają" — fields/layout/ConfigurableTable rozwiązują się
    // asynchronicznie, każde domyka `visibleColumnParams` inaczej) — bez tego lista miga
    // loaderem 2-3 razy przy każdym wejściu.
    placeholderData: keepPreviousData,
    // Awaria → jedno automatyczne ponowienie; zły adres / brak uprawnień / nieaktywny moduł →
    // bez ponawiania (komunikat od razu).
    retry: listLoadRetry,
    queryFn: async () => {
      const params = buildListFilterParams({ includeCalendar: true });
      if (visibleColumnParams.length > 0) {
        params.set('columns', visibleColumnParams.join(','));
      }
      // Paginacja serwerowa (silnik list) — `limit`/`offset` zawsze wysyłane.
      params.set('limit', String(controls.queryParams.limit));
      params.set('offset', String(controls.queryParams.offset));
      const res = await api.get<ListResponse>(`/contracts?${params.toString()}`);
      return res.data;
    },
  });
  setRefLabels(listQ.data?.extRefLabels);
  // `meta.sortable`/`meta.filterable` w stan przez EFEKT, nie w renderze (to useState → wywołanie
  // w renderze pętli).
  useEffect(() => {
    setSortableSlugs(listQ.data?.meta?.sortable);
  }, [listQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(listQ.data?.meta?.filterable);
  }, [listQ.data?.meta?.filterable]);

  // Usuwanie i przywracanie z listy to zwykły zapis dokumentu — na czas zadania przycisku SQL na nim serwer
  // odpowiada 409 `RECORD_LOCKED_BY_RUN`; zamiast ogólnego błędu pokazujemy, które zadanie blokuje dokument.
  const reportRecordLock = useReportRecordLock();

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/contracts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY] });
      toast.show({ variant: 'success', title: t('tenant.documents.contracts.toast.deleted') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      toast.show({ variant: 'error', title: t('tenant.documents.contracts.toast.deleteError') });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/contracts/${id}/restore`, {});
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['contract', id] });
      toast.show({ variant: 'success', title: t('tenant.documents.contracts.toast.restored') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      toast.show({ variant: 'error', title: t('tenant.documents.contracts.toast.restoreError') });
    },
  });

  // Akcje listy (PHASE 25) — generyczny hook: resolver widoczności (katalog ∩ kartoteka ∩
  // moduły ∩ uprawnienia) + wykonawca (rozgłoszenie po ID na istniejące endpointy) + menu
  // kontekstowe (prawy klik) + potwierdzenia. Kartoteka `documents`, klasa `contract` → tylko
  // akcje uniwersalne (KSeF/FK dotyczą wyłącznie faktur). Import bez handlera → „wkrótce".
  // BEZ bramki „Wszystkie firmy": archiwizacja/przywróć biorą firmę z samego dokumentu (nie z
  // nagłówka X-Company-Id), więc działają też w trybie unii firm.
  //
  // Eksport CSV (E16) — dokładnie to, co lista: „Eksport listy" = te same filtry (wyszukiwarka,
  // archiwum, firma, filtry kolumn, sort), wszystkie strony; „Eksport zaznaczonych" = same ID.
  // Plik: kolumny widoczne w tabeli, w jej kolejności, z nagłówkami z układu.
  const listExport = useDocumentListExport<ContractRow>({
    target: 'contracts',
    filename: 'umowy',
    // Bez miesiąca kalendarza — to nawigacja widoku, nie filtr (plik = cały widok).
    buildFilterParams: () => buildListFilterParams({ includeCalendar: false }),
    columns,
    visibleColumnIds,
    // Dopóki katalog pól i układ listy się ładują, eksport nie startuje (niepełna lista kolumn).
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extSlugSet.has(id) ? id : undefined),
    companions: CONTRACT_COLUMN_COMPANIONS,
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return columnsFor(getLabels, coreColumnsFor(getLabels), extColumnsFor(getLabels));
    },
    // „Strona" na ekranie to nazwa + dopisek NIP — w pliku „nazwa, NIP <numer>".
    textOverrides: { counterparty: counterpartyText },
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: t('tenant.documents.contracts.title'),
  });

  const listActions = useListActions({
    kartotekaKey: 'documents',
    documentClass: 'contract',
    entity: 'contracts',
    queryKey: [CONTRACTS_KEY],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    archivedView: showDeleted,
    handlers: { ...listExport.handlers },
    testIdPrefix: 'contracts',
  });

  const contracts = listQ.data?.contracts ?? [];

  return (
    <ModuleGuard moduleCode="crm_core">
      <main
        className="flex h-full min-h-0 flex-col bg-bg-secondary p-6"
        data-testid="contracts-page"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-text-primary" data-testid="contracts-title">
                {t('tenant.documents.contracts.title')}
              </h1>
              <p className="mt-1 text-sm text-text-tertiary">
                {t('tenant.documents.contracts.subtitle')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="contracts" />
              <ListActionsMenu {...listActions.menuProps} />
              <ListBuilderMenu slug="contracts" testIdPrefix="contracts" />
              <Button
                onClick={() => setShowTypePicker(true)}
                leftIcon={<Plus size={14} aria-hidden="true" />}
                data-testid="contracts-add-btn"
              >
                {t('tenant.documents.contracts.actions.add')}
              </Button>
            </div>
          </header>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {fieldsQ.isError ? (
              <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
            ) : (
              <ConfigurableTable<ContractRow>
                tableId={tableId}
                testIdPrefix="contracts"
                showOrdinal
                fillHeight
                {...viewModes.tableProps}
                /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): wyszukiwarka +
                   „Pokaż archiwalne" + baton odśwież. */
                listToolbar={
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-[240px] flex-1">
                      <ListFilterBar
                        tableId={tableId}
                        columns={columns}
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next);
                          controls.resetPage();
                        }}
                        searchPlaceholder={t('tenant.documents.contracts.searchPlaceholder')}
                        headerFilters={headerFilters}
                        onHeaderFiltersChange={handleHeaderFiltersChange}
                        onRefresh={() =>
                          queryClient.invalidateQueries({ queryKey: [CONTRACTS_KEY] })
                        }
                        showArchived={showDeleted}
                        onToggleArchived={(next) => {
                          setShowDeleted(next);
                          controls.resetPage();
                          setSelectedIds([]);
                        }}
                        testIdPrefix="contracts"
                      />
                    </div>
                  </div>
                }
                columns={columns}
                data={contracts}
                isLoading={listQ.isPending}
                getRowId={(row) => row.id}
                headerFilters={headerFilters}
                onHeaderFiltersChange={handleHeaderFiltersChange}
                filterable
                columnFilters={columnFilters}
                onColumnFiltersChange={(next) => {
                  setColumnFilters(next);
                  controls.resetPage();
                }}
                onVisibleColumnsChange={setVisibleColumnIds}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onRowContextMenu={listActions.onRowContextMenu}
                {...controls.tableProps}
                totalItems={listQ.data?.total ?? 0}
                isFetching={listQ.isFetching}
                loadError={listQ.error}
                onRetryLoad={() => void listQ.refetch()}
                onCalendarRangeChange={controls.setCalendarRange}
                onRowClick={(row) => navigate(withMenuItem(`/contracts/${row.id}`))}
                renderActions={(row) => (
                  <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {row.gcrecord !== null ? (
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmRestore(row)}
                        disabled={restoreMutation.isPending}
                        aria-label={t('tenant.documents.contracts.actions.restore')}
                        data-testid={`contracts-row-${row.id}-restore`}
                      >
                        <RotateCcw aria-hidden="true" />
                      </IconButton>
                    ) : (
                      <IconButton
                        variant="danger"
                        size="sm"
                        onClick={() => setConfirmDelete(row)}
                        disabled={deleteMutation.isPending}
                        aria-label={t('common.delete')}
                        data-testid={`contracts-row-${row.id}-delete`}
                      >
                        <Trash2 aria-hidden="true" />
                      </IconButton>
                    )}
                  </div>
                )}
                actionsColumnLabel={t('tenant.documents.contracts.columns.actions')}
                emptyIcon={FileSignature}
                emptyTitle={t('tenant.documents.contracts.empty')}
              />
            )}
          </div>
        </div>

        <ConfirmDialog
          open={confirmDelete !== null}
          title={
            confirmDelete
              ? t('tenant.documents.contracts.deleteConfirm', {
                  number:
                    confirmDelete.systemNumber ?? confirmDelete.contractNumber ?? confirmDelete.id,
                })
              : ''
          }
          message={t('tenant.documents.contracts.deleteConfirmBody')}
          confirmLabel={t('common.delete')}
          variant="danger"
          onConfirm={() => {
            if (confirmDelete) {
              deleteMutation.mutate(confirmDelete.id);
              setConfirmDelete(null);
            }
          }}
          onClose={() => setConfirmDelete(null)}
        />

        <ConfirmDialog
          open={confirmRestore !== null}
          title={
            confirmRestore
              ? t('tenant.documents.contracts.restoreConfirm', {
                  number:
                    confirmRestore.systemNumber ??
                    confirmRestore.contractNumber ??
                    confirmRestore.id,
                })
              : ''
          }
          message={t('tenant.documents.contracts.restoreConfirmBody')}
          confirmLabel={t('tenant.documents.contracts.actions.restore')}
          variant="primary"
          onConfirm={() => {
            if (confirmRestore) {
              restoreMutation.mutate(confirmRestore.id);
              setConfirmRestore(null);
            }
          }}
          onClose={() => setConfirmRestore(null)}
        />
      </main>
      <DocumentTypePickerModal
        open={showTypePicker}
        docClass="contract"
        onSelect={(typeId, pickedCompanyId) => {
          setShowTypePicker(false);
          navigate(
            withMenuItem(`/contracts/new?documentTypeId=${typeId}&companyId=${pickedCompanyId}`),
          );
        }}
        onClose={() => setShowTypePicker(false)}
      />
      {listActions.overlays}
    </ModuleGuard>
  );
}
